import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Code,
  Palette,
  HelpCircle,
  LogOut,
  Menu,
  ChevronsUpDown,
  Plus,
  Check,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOrg } from "@/hooks/useOrg";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import logo from "@/assets/supportfyi-logo.svg";

const navItems = [
  { label: "Banners", icon: LayoutDashboard, path: "/dashboard" },
  { label: "Branding", icon: Palette, path: "/branding" },
  { label: "Team", icon: Users, path: "/team" },
  { label: "Embed", icon: Code, path: "/embed" },
  { label: "Help", icon: HelpCircle, path: "/help" },
];

function OrgSwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const { org, orgs, switchOrg } = useOrg();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2 border-b border-border px-5 py-3 text-left transition-colors hover:bg-sidebar-accent">
          <span className="flex-1 truncate text-xs font-medium text-sidebar-foreground">
            {org?.name ?? "Select org"}
          </span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {orgs.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onClick={() => {
              switchOrg(o.id);
              onNavigate?.();
            }}
            className="flex items-center gap-2"
          >
            <span className="flex-1 truncate text-sm">{o.name}</span>
            {o.id === org?.id && (
              <Check className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            navigate("/create");
            onNavigate?.();
          }}
          className="flex items-center gap-2"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="text-sm">Create team</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { signOut } = useAuth();

  return (
    <>
      <div className="flex h-14 items-center border-b border-border px-5">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 text-sm font-semibold tracking-tight text-sidebar-primary"
        >
          <img src={logo} alt="supportfyi" className="h-5 w-5" />
          supportfyi
        </Link>
      </div>

      <OrgSwitcher onNavigate={onNavigate} />

      <nav className="flex flex-1 flex-col gap-1 px-3 py-3">
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-border px-3 py-3">
        <button
          onClick={signOut}
          className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );
}

export function MobileHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="flex h-14 items-center border-b border-border bg-sidebar px-4 md:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-56 p-0 bg-sidebar border-border">
          <div className="flex h-full flex-col">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
      <div className="ml-2 flex items-center gap-2">
        <img src={logo} alt="supportfyi" className="h-5 w-5" />
        <span className="text-sm font-semibold tracking-tight text-sidebar-primary">
          supportfyi
        </span>
      </div>
    </header>
  );
}

export function AppSidebar() {
  return (
    <aside className="hidden md:flex h-screen w-56 flex-col border-r border-border bg-sidebar">
      <SidebarContent />
    </aside>
  );
}
